import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useService } from '~/services/service.base';
import { UIService } from '~/services/service.ui';
import { Breadcrumb } from '~/views/molecules/nav.breadcrumbs';
import { PageWrapper } from '~/views/organisms/wrapper.page';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '~/views/molecules/page.header';
import { Grid, IconButton, Typography } from '@mui/material';
import { Icons } from '~/views/atoms/icons';
import { Column } from '~/views/atoms/boxes';
import { useSearchForm } from '~/shared/hooks/useSearchForm';
import { Collection } from '~/views/organisms/collection/collection';
import { ServiceMediaCategories } from '~/services/service.media.categories';
import { reaction } from 'mobx';
import { MediaSearchService } from '~/services/service.media.search';
import { useLocation } from 'react-router-dom';
import { CategoryProperties } from '~/views/organisms/category-properties';
import { ServiceCategoryProperties } from '~/services/service.category.properties';
import { SortingPanel } from '~/views/organisms/sorting-panel/sorting-panel';
import { ServiceSorting } from '~/services/service.sorting';
import { SortingIcon } from '~/views/atoms/svg-icons/sorting-icon';
import { useStyles } from '~/pages/funds/media/styles';
import { useActions } from '~/pages/funds/media/useActions';
import { ServiceCommunity } from '~/services/service.community';
import { EBreadcrumbKey } from '~/shared/types';
import { getBreadcrumbs } from '~/tools/breadcrumb';
import { AuthService } from '~/services/service.auth';
import { ROUTES } from '~/routes/routes';
export const PageFundsMedia = observer(() => {
    const { t } = useTranslation();
    const { breadcrumbsSvc } = useService(UIService);
    const serviceMediaCategories = useService(ServiceMediaCategories);
    const mediasSearchService = useService(MediaSearchService);
    const categoryPropertiesService = useService(ServiceCategoryProperties);
    const serviceSorting = useService(ServiceSorting);
    const { state, pathname } = useLocation();
    const communityService = useService(ServiceCommunity);
    const defaultBreadCrumbs = useMemo(() => [
        {
            key: EBreadcrumbKey.recent,
        },
    ], []);
    const authService = useService(AuthService);
    const classes = useStyles();
    const searchForm = useSearchForm();
    const { clickByCategoryHandler, clickByCategoryInfoHandler } = useActions(mediasSearchService, state, serviceMediaCategories, categoryPropertiesService, breadcrumbsSvc);
    const getCategories = useCallback(() => {
        serviceMediaCategories.setCategoryId(state?.categories?.categoryId || '0');
        serviceMediaCategories.setCommunityId(communityService.currentCommunity.id);
        serviceMediaCategories.getCategories();
    }, [communityService.currentCommunity.id, serviceMediaCategories, state?.categories?.categoryId]);
    const isSubCategory = useMemo(() => pathname === ROUTES.FUNDS.MEDIA.SUB_CATEGORY.path, [pathname]);
    const isCategoryContent = useMemo(() => pathname === ROUTES.FUNDS.MEDIA.CATEGORY_CONTENT.path, [pathname]);
    const clearData = useCallback(() => {
        mediasSearchService.clearSubCategories();
        breadcrumbsSvc.use(defaultBreadCrumbs);
        getCategories();
    }, [getCategories, breadcrumbsSvc, mediasSearchService, defaultBreadCrumbs]);
    useEffect(() => {
        getCategories();
    }, [getCategories]);
    useEffect(() => {
        if (!isSubCategory && !isCategoryContent) {
            clearData();
        }
        else if (state?.categories?.categoryId) {
            mediasSearchService.deleteSubCategory(state.categories.categoryId);
            breadcrumbsSvc.use(getBreadcrumbs(defaultBreadCrumbs, mediasSearchService));
        }
    }, [
        clearData,
        mediasSearchService,
        state?.categories,
        defaultBreadCrumbs,
        breadcrumbsSvc,
        isSubCategory,
        isCategoryContent,
    ]);
    useEffect(() => {
        if (state?.isNav === true) {
            clearData();
        }
    }, [clearData, state?.isNav]);
    useEffect(() => {
        return reaction(() => serviceMediaCategories.sorting, sorting => {
            serviceMediaCategories.getCategories(sorting);
        });
    }, [serviceMediaCategories]);
    useEffect(() => {
        breadcrumbsSvc.use(getBreadcrumbs(defaultBreadCrumbs, mediasSearchService));
    }, [breadcrumbsSvc, defaultBreadCrumbs, mediasSearchService]);
    const getPageTitle = useCallback(() => {
        if (mediasSearchService.subCategories.length > 0) {
            return mediasSearchService.subCategories[mediasSearchService.lastSubCategoryIndex].breadCrumbName;
        }
        else {
            return t('MEDIAS_PAGE_TITLE');
        }
    }, [mediasSearchService.lastSubCategoryIndex, mediasSearchService.subCategories, t]);
    return (_jsxs(PageWrapper, { children: [_jsx(Breadcrumb.Nav, { children: breadcrumbsSvc.children }), _jsxs(PageHeader, { justifyContent: "flex-start", children: [_jsx(Grid, { xs: 8, md: 9, item: true, container: true, flexDirection: "column", children: _jsx(Typography, { sx: { wordBreak: 'break-word' }, variant: "h2", children: getPageTitle() }) }), _jsx(Grid, { xs: 4, md: 3, item: true, container: true, flexDirection: "column", children: _jsxs(Grid, { container: true, justifyContent: "flex-end", children: [_jsx(IconButton, { className: classes.sortingIcon, color: "inherit", onClick: serviceSorting.openSortingService.handleOpen, children: _jsx(SortingIcon, {}) }), _jsx(IconButton, { color: "inherit", onClick: mediasSearchService.openDialogSvc.handleOpen, children: _jsx(Icons.Search, {}) })] }) })] }), _jsx(Column, { py: 1.5, gap: 2, children: _jsx(Collection, { isShowCategoryInfo: authService.isLogged, clickByCategoryHandler: clickByCategoryHandler, items: serviceMediaCategories.categories, clickByCategoryInfoHandler: clickByCategoryInfoHandler }) }), _jsx(CategoryProperties, { item: categoryPropertiesService.categoryProperties, categoryName: categoryPropertiesService.currentCategory?.title }), searchForm, serviceSorting.openSortingService.open && _jsx(SortingPanel, {})] }));
});
